import { useId } from 'react'
import type {
  AnchorHTMLAttributes,
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactNode,
  Ref,
} from 'react'
import { cx } from '@/shared/lib'
import s from './Link.module.css'

/**
 * `standalone` — самостоятельная ссылка-действие в углу блока («+ Новый адрес», «Все заказы →»).
 * `inline` — ссылка внутри абзаца: кегль наследуется, вес и подчёркивание отличают её от текста.
 */
export type LinkVariant = 'standalone' | 'inline'

/** Ступени кегля: xs 11.5 / sm 12.5 / md 13.5. У `inline` игнорируются — кегль берётся у абзаца. */
export type LinkSize = 'xs' | 'sm' | 'md'

/** `danger` — ссылка внутри красной плашки («Напомнить всем →» в баннере про просрочку). */
export type LinkTone = 'accent' | 'danger'

/** Что компонент отдаёт в слот `render`: собранный класс, содержимое и служебные атрибуты. */
export type LinkSlotProps = {
  className: string
  children: ReactNode
  'data-variant': LinkVariant
  'data-size': LinkSize
  'data-tone': LinkTone
}

type LinkBaseProps = {
  /** Подпись. Стрелка «→» и плюс «+» из кита сюда не попадают — они живут в слотах иконок. */
  children: ReactNode
  variant?: LinkVariant
  size?: LinkSize
  tone?: LinkTone
  /** Слот перед текстом: `<Icon name="plus" size="xs" />` у «Новый адрес». */
  iconLeft?: ReactNode
  /** Слот после текста: `<Icon name="chevronRight" size="xs" />` вместо китовой стрелки. */
  iconRight?: ReactNode
  className?: string
}

/**
 * Новая вкладка объявляется скринридеру до перехода, поэтому `external` без текста
 * предупреждения не собирается: строк внутри `shared/ui` не бывает.
 */
type LinkExternalProps =
  { external: true; externalLabel: string } | { external?: false; externalLabel?: never }

/** Есть адрес — это `<a>`: средний клик, «открыть в новой вкладке», контекстное меню, «Назад». */
type LinkAnchorProps = LinkBaseProps &
  LinkExternalProps & {
    href: string
    render?: never
    disabled?: never
    ref?: Ref<HTMLAnchorElement>
  } & Omit<
    AnchorHTMLAttributes<HTMLAnchorElement>,
    'href' | 'target' | 'rel' | 'children' | 'color'
  >

/**
 * Навигация роутера. Примитив о роутере не знает: он собирает класс и содержимое,
 * а элемент создаёт потребитель на слое, где роутер уже известен.
 */
type LinkRouterProps = LinkBaseProps & {
  render: (props: LinkSlotProps) => ReactNode
  href?: never
  external?: never
  externalLabel?: never
  disabled?: never
  onClick?: never
  ref?: never
}

/** Адреса нет — это действие на текущей странице, то есть `<button type="button">`. */
type LinkButtonProps = LinkBaseProps & {
  href?: never
  render?: never
  external?: never
  externalLabel?: never
  onClick: MouseEventHandler<HTMLButtonElement>
  disabled?: boolean
  ref?: Ref<HTMLButtonElement>
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children' | 'onClick' | 'color'>

export type LinkProps = LinkAnchorProps | LinkRouterProps | LinkButtonProps

// CSS-модуль типизирован индексной сигнатурой, поэтому значения — string | undefined
const VARIANT_CLASS: Record<LinkVariant, string | undefined> = {
  standalone: s.standalone,
  inline: s.inline,
}

const SIZE_CLASS: Record<LinkSize, string | undefined> = {
  xs: s.xs,
  sm: s.sm,
  md: s.md,
}

const TONE_CLASS: Record<LinkTone, string | undefined> = {
  accent: s.toneAccent,
  danger: s.toneDanger,
}

/*
 * Ветки различаются не литеральным дискриминантом, а наличием href/render, поэтому
 * сужение делается предикатами: `href: string` — не unit-тип, автоматически TS его не сузит.
 */
const isRouterLink = (props: LinkProps): props is LinkRouterProps => props.render !== undefined
const isAnchorLink = (props: LinkProps): props is LinkAnchorProps => props.href !== undefined

function viewProps(
  variant: LinkVariant,
  size: LinkSize,
  tone: LinkTone,
  className: string | undefined,
): Omit<LinkSlotProps, 'children'> {
  return {
    className: cx(
      s.root,
      VARIANT_CLASS[variant],
      // У ссылки в абзаце своего кегля нет: она берёт его у текста вокруг
      variant === 'standalone' && SIZE_CLASS[size],
      TONE_CLASS[tone],
      className,
    ),
    'data-variant': variant,
    'data-size': size,
    'data-tone': tone,
  }
}

type LinkContentProps = {
  iconLeft?: ReactNode
  iconRight?: ReactNode
  externalLabel?: string
  externalHintId?: string
  children: ReactNode
}

function LinkContent({
  iconLeft,
  iconRight,
  externalLabel,
  externalHintId,
  children,
}: LinkContentProps) {
  return (
    <>
      {iconLeft === undefined ? null : <span className={s.icon}>{iconLeft}</span>}
      <span className={s.text}>{children}</span>
      {iconRight === undefined ? null : <span className={s.icon}>{iconRight}</span>}
      {/*
        Предупреждение о новой вкладке — описание, а не часть имени: приклеенный к имени текст
        склеивался бы с подписью без пробела там, где стили не применились, и мешал бы
        голосовому управлению искать ссылку по видимой подписи.
      */}
      {externalLabel === undefined ? null : (
        /*
         * aria-hidden не мешает описанию: узел, на который прямо ссылается aria-describedby,
         * алгоритм читает даже скрытым. Зато он не попадает в имя ссылки вторым куском.
         */
        <span id={externalHintId} className={s.externalHint} aria-hidden="true">
          {externalLabel}
        </span>
      )}
    </>
  )
}

function AnchorLink({
  href,
  external,
  externalLabel,
  variant = 'standalone',
  size = 'sm',
  tone = 'accent',
  iconLeft,
  iconRight,
  className,
  children,
  ref,
  'aria-describedby': describedBy,
  ...rest
}: LinkAnchorProps) {
  const hintId = useId()

  return (
    <a
      {...rest}
      ref={ref}
      {...viewProps(variant, size, tone, className)}
      href={href}
      /* target и rel в API не пускаются: «новая вкладка» без noopener появиться не должна */
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      /* Описание потребителя не затирается: своё добавляется к нему, а не вместо него */
      aria-describedby={cx(describedBy, external && hintId) || undefined}
    >
      <LinkContent
        iconLeft={iconLeft}
        iconRight={iconRight}
        externalLabel={externalLabel}
        externalHintId={hintId}
      >
        {children}
      </LinkContent>
    </a>
  )
}

function ButtonLink({
  onClick,
  disabled = false,
  variant = 'standalone',
  size = 'sm',
  tone = 'accent',
  iconLeft,
  iconRight,
  className,
  children,
  ref,
  ...rest
}: LinkButtonProps) {
  return (
    <button
      {...rest}
      ref={ref}
      {...viewProps(variant, size, tone, className)}
      /* Текстовая ссылка внутри формы её не отправляет: нужен submit — это Button */
      type="button"
      disabled={disabled}
      onClick={onClick}
    >
      <LinkContent iconLeft={iconLeft} iconRight={iconRight}>
        {children}
      </LinkContent>
    </button>
  )
}

function SlotLink({
  render,
  variant = 'standalone',
  size = 'sm',
  tone = 'accent',
  iconLeft,
  iconRight,
  className,
  children,
}: LinkRouterProps) {
  return render({
    ...viewProps(variant, size, tone, className),
    children: (
      <LinkContent iconLeft={iconLeft} iconRight={iconRight}>
        {children}
      </LinkContent>
    ),
  })
}

/**
 * Текстовый переход и текстовое действие: «+ Новый адрес», «Все заказы →», «Показать снова».
 *
 * Тег не выбирается пропом, а выводится из данных: `href` → `<a>`, `render` → элемент роутера,
 * `onClick` → `<button type="button">`. Кликабельного `<span>` из кита, недостижимого
 * с клавиатуры, компонент не собирает ни при каком наборе пропсов.
 *
 * Контракт: docs/ui-kit/contracts/link.md
 */
export function Link(props: LinkProps) {
  if (isRouterLink(props)) return <SlotLink {...props} />
  if (isAnchorLink(props)) return <AnchorLink {...props} />
  return <ButtonLink {...props} />
}
